import type { FormField, InterviewSession } from '../../types';
import { standaloneFormBlock, cloneConversationSections, normalizeConversationSections, validateConversationSections, type ConversationSection, type FormBlock } from '../../lib/conversation-sections';
import { cloneFormFields, FORM_FIELD_LIMITS, validateFormFields } from '../../lib/form-capabilities';
import { generateFieldId } from '../../lib/form-templates';
import { questionConditionIssue, type QuestionCondition } from '../../lib/question-conditions';
import { insertSectionFields, type BuilderDefinition } from './sections/section-editor';

/** Saved source definition selected from the owning workspace. */
export type NestedFormSource = Pick<InterviewSession, 'id' | 'target_name' | 'form_schema' | 'conversation_config' | 'require_video' | 'require_name' | 'require_email' | 'identity_collection'>;
/** Insert at the chosen plus, preserving the host section and every existing answer key. */
interface NestedFormInsertion extends BuilderDefinition {
  index: number;
  sectionId?: string;
  source: NestedFormSource;
  showWhen?: QuestionCondition;
}

/** Explain why a source cannot be collected inside a parent response. */
export function nestedSourceIssue(source: NestedFormSource): string | null {
  if (source.require_video) return 'Choose a form without required video.';
  if ((source.identity_collection === 'pre' || source.identity_collection === 'post') && (source.require_name || source.require_email)) {
    return 'Choose a form without separate identity collection.';
  }
  if (!source.conversation_config || !source.form_schema?.length) return 'Choose a form with published questions.';
  if (source.conversation_config.sections?.some(section => section.blocks?.length)) return 'Choose a form without another nested form.';
  if (source.form_schema.some(field => ['calendar_booking', 'signature', 'file_upload'].includes(field.type))) {
    return 'Choose a form without bookings, signatures or file uploads.';
  }
  const issue = validateFormFields(source.form_schema)[0] ?? validateConversationSections(source.form_schema, source.conversation_config.sections)[0];
  return issue ? 'The source questions need attention before they can be nested.' : null;
}

/** Child guest bindings belong to the source response, and parent answer keys must be unique. */
function copyChildFields(fields: readonly FormField[]): FormField[] {
  return cloneFormFields(fields).map(field => {
    if ('profileBinding' in field) delete field.profileBinding;
    field.name = `nested_${field.id.replaceAll('-', '')}`;
    return field;
  });
}

/** Attach a nested definition to an existing section or a dedicated invisible owner. */
function insertBlock(fields: FormField[], sections: ConversationSection[], index: number, additions: FormField[], block: FormBlock, sectionId?: string): BuilderDefinition {
  const ownerId = sectionId ?? `nested_${block.id}`;
  if (sectionId && !sections.some(section => section.id === sectionId)) throw new Error('The section was removed.');
  const owners = sectionId ? sections : [...sections, { id: ownerId, title: block.title, fieldIds: [] }];
  const result = insertSectionFields(fields, owners, index, additions, ownerId);
  result.sections = result.sections.map(section => section.id === ownerId ? { ...section, blocks: [...section.blocks ?? [], block] } : section);
  return result;
}

/** Embed a pinned source definition once at the selected position. */
export function insertNestedForm(input: NestedFormInsertion): BuilderDefinition {
  const { source, index, showWhen } = input;
  const issue = nestedSourceIssue(source);
  if (issue) throw new Error(issue);
  const originals = source.form_schema!;
  if (input.fields.length + originals.length > FORM_FIELD_LIMITS.fields) throw new Error('Use at most 200 steps across this form and its blocks.');
  if (showWhen && questionConditionIssue(showWhen, input.fields.slice(0, index))) throw new Error('Complete the nested form condition.');
  const additions = copyChildFields(originals);
  const sourceSections = source.conversation_config?.sections;
  const block: FormBlock = { id: generateFieldId(), title: source.target_name.trim().slice(0, 200), sourceId: source.id,
    sourceFields: structuredClone(originals), ...(sourceSections ? { sourceSections: structuredClone(sourceSections) } : {}),
    fieldIds: additions.map(field => field.id), sections: cloneConversationSections(sourceSections ?? [], originals, additions),
    ...(showWhen ? { showWhen } : {}) };
  const next = insertBlock(input.fields, input.sections, index, additions, block, input.sectionId);
  const problem = validateConversationSections(next.fields, next.sections).find(i => next.sections.find(s => s.id === i.sectionId)?.blocks?.includes(block));
  if (problem) throw new Error(problem.message);
  return next;
}

/** Resolve a nested form's ordered parent fields without acting on a stale insertion point. */
function locateBlock(fields: FormField[], sections: ConversationSection[], id: string) {
  const owner = sections.find(section => section.blocks?.some(block => block.id === id));
  const block = owner?.blocks?.find(candidate => candidate.id === id);
  if (!owner || !block) throw new Error('The block was removed.');
  const index = fields.findIndex(field => field.id === block.fieldIds[0]);
  if (index < 0 || !block.fieldIds.length || block.fieldIds.some((id, offset) => fields[index + offset]?.id !== id)) {
    throw new Error('Keep the block’s questions together.');
  }
  return { owner, block, index, members: fields.slice(index, index + block.fieldIds.length) };
}

/** Duplicate the nested form after its last question with fresh parent-owned identities. */
export function duplicateBlock(fields: FormField[], sections: ConversationSection[], id: string): BuilderDefinition {
  const { owner, block, members, index } = locateBlock(fields, sections, id);
  if (fields.length + members.length > FORM_FIELD_LIMITS.fields) throw new Error('Use at most 200 steps across this form and its blocks.');
  const additions = copyChildFields(members);
  const [copiedSection] = cloneConversationSections([{ ...owner, fieldIds: block.fieldIds, blocks: [block] }], members, additions);
  return insertBlock(fields, sections, index + members.length, additions, copiedSection.blocks![0], standaloneFormBlock(owner) ? undefined : owner.id);
}

/** Remove this embed atomically while preserving original source assets. */
export function removeBlock(fields: FormField[], sections: ConversationSection[], id: string): BuilderDefinition {
  const { owner, block, members, index } = locateBlock(fields, sections, id);
  const next = [...fields.slice(0, index), ...fields.slice(index + members.length)];
  const removed = new Set(block.fieldIds);
  const groups = sections.flatMap(section => {
    if (section.id !== owner.id) return [section];
    if (standaloneFormBlock(section)) return [];
    const { blocks, ...metadata } = section;
    const retained = (blocks ?? []).filter(item => item.id !== id);
    return [{ ...metadata, fieldIds: metadata.fieldIds.filter(fieldId => !removed.has(fieldId)), ...(retained.length ? { blocks: retained } : {}) }];
  });
  return { fields: next, sections: normalizeConversationSections(next, groups) };
}
